/**
 * stageMoveGate — the single seam that decides what a card move into a stage
 * must collect from the user before the move is allowed to hit the network
 * (SE-5495).
 *
 * Why a seam and not an inline `phase.isProtected` check at the move site:
 * the product decision is that "this stage needs a reason" is a **configurable
 * rule** authored on the Rules page, not an intrinsic property of the stage.
 * That configuration surface does not exist in the shipped schema yet —
 * `PipelineAutomationActionType` has no rationale-requiring action, and the
 * only per-stage sensitivity signal the API exposes today is
 * `KanbanPhase.isProtected`.
 *
 * So the resolution lives here, in ONE function, and nowhere else. When the
 * rules engine lands, `resolveStageMoveGate` starts reading the configured
 * rule for the target stage and every caller — board drag today, the player
 * drawer and the player profile later — inherits the new behaviour without a
 * single edit to the move path.
 *
 * The gate is deliberately returned as a shape rather than a boolean.
 * SE-5496 (terminal-stage outcome selector) adds `requiresOutcome` semantics
 * on top of the same prompt; keeping the field in the contract from day one
 * means that ticket adds a field to the prompt instead of replacing the
 * component and re-plumbing every call site.
 * @module features/player-kanban/utils/stageMoveGate
 */

import type { KanbanPhase } from "@/generated/graphql";

/**
 * The minimum shape `resolveStageMoveGate` needs. Deliberately narrower than
 * `KanbanPhase` so callers holding a partial phase (a cache-normalised
 * `kanbanPhase { id name }` selection, a table row projection) can still be
 * type-checked against the seam rather than casting.
 */
export type StageMoveGateInput =
  | Partial<Pick<KanbanPhase, "isProtected" | "isTerminal">>
  | null
  | undefined;

/**
 * What the user must supply before a move into a given stage may proceed.
 */
export interface StageMoveGate {
  /** The move must collect a free-text reason before the mutation fires. */
  readonly requiresRationale: boolean;
  /**
   * The move must additionally collect a terminal outcome (SIGNED, PASSED, …).
   * Resolved from `KanbanPhase.isTerminal` (SE-5496). A terminal target
   * requires BOTH an outcome AND a reason.
   */
  readonly requiresOutcome: boolean;
}

/** A move that needs nothing from the user — the frictionless path. */
export const NO_STAGE_MOVE_GATE: StageMoveGate = {
  requiresRationale: false,
  requiresOutcome: false,
};

/**
 * Whether entering this stage requires the mover to record a reason.
 *
 * Today: resolved from `KanbanPhase.isProtected`. Tomorrow: resolved from the
 * stage's configured Rules-page rule. This is the ONLY place that mapping is
 * expressed.
 * @param phase - The stage being entered (may be partial or absent).
 * @returns `true` when a reason prompt must be shown before the move.
 */
export const stageRequiresRationale = (phase: StageMoveGateInput): boolean =>
  phase?.isProtected === true;

/**
 * Whether entering this stage requires a terminal outcome selection.
 *
 * Resolved from `KanbanPhase.isTerminal` (SE-5496): the terminal ticket
 * switches the outcome capture on here, in the single place that maps the
 * stage's shape to what the move must collect. `isProtected` → reason
 * (SE-5495); `isTerminal` → outcome (this function). A terminal stage requires
 * both — the reason gate stays independently true because a terminal close
 * demands a reason as well.
 * @param phase - The stage being entered (may be partial or absent).
 * @returns `true` when the target stage is terminal.
 */
export const stageRequiresOutcome = (phase: StageMoveGateInput): boolean =>
  phase?.isTerminal === true;

/**
 * Resolve the full gate for a move into `phase`.
 * @param phase - The stage being entered (may be partial or absent).
 * @returns The gate describing what the move must collect first.
 */
export const resolveStageMoveGate = (
  phase: StageMoveGateInput
): StageMoveGate => ({
  requiresRationale: stageRequiresRationale(phase),
  requiresOutcome: stageRequiresOutcome(phase),
});

/**
 * Whether a gate demands any user interaction at all.
 *
 * The move path uses this to stay **synchronous** for ungated moves: an
 * `await` before the optimistic `onMoveStart` would defer the override past
 * the caller's `flushSync`, reintroducing the SE-4272 one-frame snap-back on
 * every ordinary drag.
 * @param gate - A resolved gate.
 * @returns `true` when the move must be paused for a prompt.
 */
export const gateRequiresPrompt = (gate: StageMoveGate): boolean =>
  gate.requiresRationale || gate.requiresOutcome;
